// src/admin/components/access-urls/ShortUrlCodeDisplay.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import CopyButton from '../common/CopyButton';
import { QR_BASE_URL } from '../../../config/env';

// Base URL of the public short URL / QR service. Configurable via
// REACT_APP_QR_BASE_URL so dev environments can point at a local
// hostname (e.g. http://qr.localhost:3000) and tests can scan/copy
// URLs that actually resolve. Defaults to the production host so
// the prod build behaves correctly with no extra config.

/**
 * Displays a short URL code with clickable link and copy button.
 * - Claimed URLs: Opens the asset page on the user side (full redirect URL)
 * - Unclaimed URLs: Navigates to the claim page within admin
 */
export default function ShortUrlCodeDisplay({ code, claimed, invalidated, accessKeyToken }) {
  const navigate = useNavigate();

  const fullUrl = `${window.location.origin}/assets/access_key/${accessKeyToken}`;
  const qrUrl = `${QR_BASE_URL}/${code}`;

  const handleClick = (e) => {
    e.preventDefault();
    if (invalidated) {
      return; // Don't do anything for invalidated URLs
    }
    if (claimed && accessKeyToken) {
      // Open the user-side asset page in a new tab
      window.open(fullUrl, '_blank');
    } else {
      // Navigate to the claim page within admin
      navigate(`/admin/short-urls/claim/${code}`);
    }
  };

  return (
    <div className="d-flex align-items-center gap-2">
      <button
        type="button"
        className={`btn btn-link p-0 font-monospace text-start ${invalidated ? 'text-muted text-decoration-line-through' : ''}`}
        onClick={handleClick}
        disabled={invalidated}
        title={invalidated ? 'This URL has been invalidated' : claimed ? 'Open asset page' : 'Claim this URL'}
      >
        {code}
      </button>
      {!invalidated && (
        <CopyButton
          value={qrUrl}
          title="Copy short URL"
        />
      )}
    </div>
  );
}

ShortUrlCodeDisplay.propTypes = {
  code: PropTypes.string.isRequired,
  claimed: PropTypes.bool,
  invalidated: PropTypes.bool,
  accessKeyToken: PropTypes.string,
};
